import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Quels sont les signes que mon store banne a besoin d'une réparation ?",
    a: "Les signes courants incluent : une toile qui se déchire ou se décolore, des bras qui grincent ou se bloquent, un moteur qui tourne dans le vide, une télécommande qui ne répond plus, ou un store qui s'arrête en cours de manœuvre. Si vous constatez l'un de ces symptômes, appelez-nous pour un diagnostic.",
  },
  {
    q: "Pouvez-vous remplacer uniquement la toile de mon store banne à Lyon ?",
    a: "Oui, nous remplaçons les toiles de store banne. Nous prenons les mesures précises de votre store, vous proposons un choix de toiles acryliques de qualité (Dickson, Sunbrella, etc.) et assurons la pose. C'est une solution bien plus économique que de changer l'ensemble du store.",
  },
  {
    q: "Combien de temps dure une réparation de store banne ?",
    a: "La plupart des réparations courantes (remplacement de toile, réparation de bras, changement de moteur) sont réalisées en une demi-journée sur place. Pour les pièces spécifiques, un délai de commande de quelques jours peut être nécessaire.",
  },
];

export default function ReparationStoreBanne() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Réparation de store banne à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Réparation de store banne à Lyon : toile, bras, moteur, coffre. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Réparation store à Lyon', item: 'https://depanfenetres-lyon.fr/reparation-store-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Réparation store banne', item: 'https://depanfenetres-lyon.fr/reparation-store-banne-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Réparation de store banne à Lyon | DFPRO"
        description="Store banne en panne à Lyon ? Réparation toile, bras, moteur et coffre. Remplacement de toile. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans. DFPRO."
        canonicalUrl="https://depanfenetres-lyon.fr/reparation-store-banne-lyon/"
        ogTitle="Réparation de store banne à Lyon — Toile, bras, moteur | DFPRO"
        ogDescription="Store banne abîmé ? Réparation et remplacement de toile à Lyon. Devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/reparation-store-lyon/" className="hover:text-primary-500 transition-colors">Réparation store à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Réparation store banne</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/store-banne-rep-hero-2026.jpg"
              alt="Réparation de store banne à Lyon par DFPRO Lyon — technicien intervenant sur un store de terrasse"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Réparation de store banne à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Votre store banne est déchiré, bloqué ou ne répond plus ? Nous réparons toiles, bras, moteurs et mécanismes à Lyon. Intervention rapide 7j/7, devis gratuit.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Store banne en panne à Lyon ? Diagnostic et réparation
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Le store banne est l'équipement de protection solaire le plus répandu sur les terrasses et balcons lyonnais. Soumis aux intempéries, au vent et au soleil intense de la région, il peut présenter différents types de pannes au fil du temps. <strong className="text-foreground-800">DFPRO Lyon intervient pour diagnostiquer et réparer</strong> votre store banne, quelle que soit la marque ou l'ancienneté de l'équipement.
                  </p>
                  <p>
                    Les pannes les plus fréquentes que nous rencontrons à Lyon concernent la <strong className="text-foreground-800">toile</strong> : déchirure, décoloration, coutures qui lâchent, ou toile qui se déforme et ne s'enroule plus correctement. Une toile abîmée n'est pas seulement inesthétique, elle perd aussi son efficacité protectrice contre le soleil. Nous proposons le <strong className="text-foreground-800">remplacement de toile sur mesure</strong> avec des tissus acryliques haute résistance (Dickson, Sunbrella, Para, Sattler) garantis anti-UV et déperlants.
                  </p>
                  <p>
                    Les <strong className="text-foreground-800">bras articulés</strong> sont également sujets à l'usure. Un bras qui grince, qui se bloque en position ouverte ou fermée, ou qui ne maintient plus la tension de la toile est un signe qu'une intervention est nécessaire. Nous réparons ou remplaçons les bras, les articulations, les ressorts de compensation et les vérins de votre store banne.
                  </p>
                  <p>
                    Enfin, la <strong className="text-foreground-800">motorisation</strong> est une source courante de panne : moteur qui ne tourne plus ou qui tourne dans le vide, télécommande déprogrammée, fin de course déréglé, capteur vent qui bloque le store en permanence. Nos techniciens maîtrisent les motorisations Somfy, Simu, Bubendorff et autres marques pour un diagnostic et une réparation rapides.
                  </p>
                  <p>
                    Nous intervenons à Lyon et dans toute la Métropole de Lyon : Villeurbanne, Caluire-et-Cuire, Vénissieux, Bron, Saint-Priest, Tassin-la-Demi-Lune, Écully, Sainte-Foy-lès-Lyon et toutes les communes limitrophes. Chaque réparation bénéficie de notre <strong className="text-foreground-800">garantie 10 ans</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services store</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Réparation store à Lyon (pilier)', href: '/reparation-store-lyon/' },
                    { label: 'Store banne bloqué', href: '/store-banne-bloque-lyon/' },
                    { label: 'Motorisation de store', href: '/motorisation-store-lyon/' },
                    { label: 'Réparation BSO', href: '/reparation-bso-lyon/' },
                    { label: 'Store électrique', href: '/store-electrique-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Store banne à réparer à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84. Toile, bras, moteur : on s'occupe de tout ! Devis gratuit."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}